/**
 * useStylingAssistant Hook
 * Manages all state and logic for the Styling Assistant
 * - Handles streaming API calls via ENDPOINTS.STYLING_ASSISTANT
 * - Manages chat messages, loading states, progressive display
 * - Tracks chat_id for conversation continuity
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { APP_CONFIG } from '../config/app.config';
import { streamStylingSearch } from '../api/stylingAssistant';
import type {
  SAChatMessage,
  SAProductsMap,
  SADisplayGroup,
  SAProductsStreamPayload,
  SASummaryStreamPayload,
  SACompleteStreamPayload,
} from '../types/stylingAssistant.types';

// ─── Default search suggestions ──────────────────────────────────────────────

const DEFAULT_SEARCH_SUGGESTIONS = [
  'Show me elegant cocktail dresses for a night event',
  'Looking for structured handbags in a modern aesthetic',
  'I need blue dresses with a refined silhouette',
  'Show me women\'s tops without buttons',
  'I am looking for winter outfits',
  'Men\'s tops between 50 and 150 dollars',
  'Casual summer dresses under $200',
  'Looking for leather jackets for women',
];

// ─── Helper: Build display groups from products map ──────────────────────────

export function buildDisplayGroups(productsMap: SAProductsMap): SADisplayGroup[] {
  const categoryKeys = Object.keys(productsMap);
  if (categoryKeys.length === 0) return [];

  return categoryKeys
    .filter((category) => productsMap[category] && productsMap[category].length > 0)
    .map((category) => ({
      title: category,
      products: productsMap[category],
    }));
}

// ─── Helper: Generate session UUID ───────────────────────────────────────────

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useStylingAssistant() {
  // Use APP_CONFIG for userId
  const userId = APP_CONFIG.APPAREL_USER_ID;

  // Search suggestions
  const searchItems = DEFAULT_SEARCH_SUGGESTIONS;

  // ─── State ──────────────────────────────────────────────────────────────────

  const [messages, setMessages] = useState<SAChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingPhase, setStreamingPhase] = useState<'idle' | 'products' | 'summary' | 'done'>('idle');
  const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
  const [visibleGroupIndexes, setVisibleGroupIndexes] = useState<number[]>([]);
  const [isProgressivelyDisplaying, setIsProgressivelyDisplaying] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [chatId, setChatId] = useState(() => generateSessionId());

  // ─── Refs ───────────────────────────────────────────────────────────────────

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const carouselRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollTimeoutRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const progressiveTimersRef = useRef<number[]>([]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      progressiveTimersRef.current.forEach(clearTimeout);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // ─── Scroll to bottom (non-progressive) ─────────────────────────────────────

  useEffect(() => {
    if (messagesEndRef.current && !isProgressivelyDisplaying) {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  }, [messages, isProgressivelyDisplaying]);

  // ─── Auto-scroll when new groups appear progressively ────────────────────────

  useEffect(() => {
    if (visibleGroupIndexes.length > 0 && isProgressivelyDisplaying) {
      const latestIndex = visibleGroupIndexes[visibleGroupIndexes.length - 1];
      const el = groupRefs.current[latestIndex];
      if (el) {
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = window.setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    }
  }, [visibleGroupIndexes, isProgressivelyDisplaying]);

  // ─── Progressive display of groups ──────────────────────────────────────────

  const startProgressiveDisplay = useCallback((groupCount: number) => {
    progressiveTimersRef.current.forEach(clearTimeout);
    progressiveTimersRef.current = [];

    setIsProgressivelyDisplaying(true);
    setVisibleGroupIndexes([]);

    for (let i = 0; i < groupCount; i++) {
      const timer = window.setTimeout(() => {
        setVisibleGroupIndexes((prev) => [...prev, i]);
        if (i === groupCount - 1) {
          window.setTimeout(() => setIsProgressivelyDisplaying(false), 500);
        }
      }, i * 1500);
      progressiveTimersRef.current.push(timer);
    }
  }, []);

  // ─── Streaming Search ───────────────────────────────────────────────────────

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim() || isLoading) return;

      // Abort any previous stream
      abortControllerRef.current?.abort();
      progressiveTimersRef.current.forEach(clearTimeout);

      // Add user message
      const userMessage: SAChatMessage = {
        id: Date.now().toString(),
        type: 'user',
        text: searchTerm.trim(),
      };

      // Create placeholder assistant message
      const assistantMessageId = (Date.now() + 1).toString();
      const assistantMessage: SAChatMessage = {
        id: assistantMessageId,
        type: 'assistant',
        text: '',
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setActiveMessageId(assistantMessageId);
      setInputValue('');
      setShowSuggestionsDropdown(false);
      setIsLoading(true);
      setStreamingPhase('idle');
      setVisibleGroupIndexes([]);
      groupRefs.current = {};
      carouselRefs.current = {};

      // Start streaming
      const controller = streamStylingSearch(searchTerm, userId, chatId, {
        onProductsReceived: (payload: SAProductsStreamPayload) => {
          setStreamingPhase('products');

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    productsMap: payload.products,
                    categories: payload.categories,
                    chatHistoryId: payload.chat_history_id,
                    metadata: {
                      intentType: payload.intent_type,
                      rewrittenQuery: payload.rewritten_query,
                      gender: payload.gender,
                      season: payload.season,
                      colors: payload.colors,
                    },
                  }
                : msg
            )
          );

          startProgressiveDisplay(Object.keys(payload.products).length);
          setIsLoading(false);
        },

        onSummaryReceived: (payload: SASummaryStreamPayload) => {
          setStreamingPhase('summary');

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    text: payload.summary || '',
                    summary: payload.summary,
                    followUpQuestions: payload.follow_up_questions,
                  }
                : msg
            )
          );
        },

        onCompletePayloadReceived: (payload: SACompleteStreamPayload) => {
          setStreamingPhase('products');

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    productsMap: payload.products,
                    categories: payload.categories,
                    chatHistoryId: payload.chat_history_id,
                    text: payload.summary || '',
                    summary: payload.summary,
                    followUpQuestions: payload.follow_up_questions,
                    metadata: {
                      intentType: payload.intent_type,
                      rewrittenQuery: payload.rewritten_query,
                      gender: payload.gender,
                      season: payload.season,
                      colors: payload.colors,
                    },
                  }
                : msg
            )
          );

          startProgressiveDisplay(Object.keys(payload.products).length);
          setIsLoading(false);
          setStreamingPhase('done');
        },

        onError: (error: Error) => {
          console.error('[SA Hook] Stream error:', error);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    text: "I'm sorry, something went wrong. Please try again.",
                  }
                : msg
            )
          );
          setIsLoading(false);
          setStreamingPhase('done');
        },

        onStreamEnd: () => {
          setStreamingPhase('done');
        },
      });

      abortControllerRef.current = controller;
    },
    [isLoading, userId, chatId, startProgressiveDisplay]
  );

  // ─── Follow-up question click ───────────────────────────────────────────────

  const handleFollowUpClick = useCallback(
    (question: string) => {
      handleSearch(question);
    },
    [handleSearch]
  );

  // ─── Suggestion click ──────────────────────────────────────────────────────

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      handleSearch(suggestion);
    },
    [handleSearch]
  );

  // ─── Send ──────────────────────────────────────────────────────────────────

  const handleSend = useCallback(() => {
    if (!inputValue.trim()) return;
    handleSearch(inputValue);
  }, [inputValue, handleSearch]);

  // ─── Key press ─────────────────────────────────────────────────────────────

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // ─── Refresh ───────────────────────────────────────────────────────────────

  const handleRefresh = useCallback(() => {
    abortControllerRef.current?.abort();
    progressiveTimersRef.current.forEach(clearTimeout);
    setMessages([]);
    setInputValue('');
    setShowSuggestionsDropdown(false);
    setVisibleGroupIndexes([]);
    setIsProgressivelyDisplaying(false);
    setIsLoading(false);
    setStreamingPhase('idle');
    setChatId(generateSessionId());
    groupRefs.current = {};
    carouselRefs.current = {};
  }, []);

  // ─── Carousel scroll ──────────────────────────────────────────────────────

  const scrollCarousel = useCallback((groupIndex: number, direction: 'left' | 'right') => {
    const carousel = carouselRefs.current[groupIndex];
    if (carousel) {
      const scrollAmount = 400;
      carousel.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  // ─── Return ─────────────────────────────────────────────────────────────────

  return {
    // State
    messages,
    inputValue,
    isLoading,
    streamingPhase,
    showSuggestionsDropdown,
    visibleGroupIndexes,
    isProgressivelyDisplaying,
    activeMessageId,
    searchItems,

    // Setters
    setInputValue,
    setShowSuggestionsDropdown,

    // Refs
    inputRef,
    messagesEndRef,
    groupRefs,
    carouselRefs,

    // Actions
    handleSearch,
    handleSend,
    handleKeyPress,
    handleRefresh,
    handleSuggestionClick,
    handleFollowUpClick,
    scrollCarousel,

    // Helpers
    buildDisplayGroups,
  };
}
